import { Button, Col, Form, FormControl, FormLabel, FormSelect, Modal, Row, Tab, Tabs } from "react-bootstrap";
import { IVersionSection } from "../../../models/Interfaces";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Core, formValidation, notEmpty } from "@form-validation/bundle/full";
import AceEditor from "react-ace";
import 'ace-builds/src-noconflict/ext-command_bar';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-sqlserver';
import ace from 'ace-builds/src-noconflict/ace';
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import ColourPicker from "../../components/common/ColourPicker";

import workerJsUrl from 'ace-builds/src-noconflict/worker-javascript.js?url';

ace.config.setModuleUrl('ace/mode/javascript_worker', workerJsUrl);

const TemplateType = {
  ColumnsFW: "ColumnsFullWidth",
  ColumnsCC: "Columns",
  ContentFW: "FullWidthContent",
  ContentCC: "CenteredContent",
};

interface IAddEditSection {
  isOpen: boolean;
  isNew: boolean;
  onClose: () => void;
  onSubmit: (sectiondata: IVersionSection) => void;
  section: IVersionSection;
  above?: boolean;
}

const TabNames = {
  General: "General",
  Advanced: "Advanced"
}

const AddEditSection = (props: IAddEditSection) => {
  const [sectionDetail, setSectionDetail] = useState<IVersionSection>(props.section);
  const [activeTab, setActiveTab] = useState<string>(TabNames.General);

  const fvRef = useRef<Core | null>(null);
  const formRef = useRef(null);

  const setInput = (evt: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = evt.target;

    setSectionDetail((section: IVersionSection) => ({
      ...section,
      [name]: value
    }));
  };

  const setSelectInput = (evt: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = evt.target;
    setSectionDetail((section: IVersionSection) => ({
      ...section,
      [name]: value
    }));    
  };

  const setColumns = (evt: ChangeEvent<HTMLSelectElement>) : void => {
    const val = evt.target.value;

    if (!isNaN(Number(val))) {
      setSectionDetail((section: IVersionSection) => ({
        ...section,
        Columns: Number(val)
      }));
    }
  };

  const setCodeInput = (val: string) : void => {
    setSectionDetail((section: IVersionSection) => ({
      ...section,
      ClientScript: val
    }));
  };

  const updateProperty = (name: string, value: string) : void => {
    setSectionDetail(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSave = async () : Promise<void> => {
    if (fvRef.current) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        props.onSubmit(sectionDetail);
      }
    }
  };

  const setValidation = () : void => {
    const form = document.getElementById("SectionPropertiesForm") as HTMLFormElement | null;

    if (form) {
      fvRef.current = createFormValidation(form, {
        fields: {
          Template: {
            validators: {
              notEmpty: { message: "Select the template for this section"}
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton()
        }
      });
    }
  };

  useEffect(() => {
    if (!fvRef.current) {
      setValidation();
    }
  });

  if (!props.isOpen) {
    return null;
  }

  const title = `${props.section.Id === 0 ? "New" : ""} Section Details`;

  return (
    <Modal show={props.isOpen} onHide={props.onClose} size="xl" backdrop="static">
      <Modal.Header className="modal-content hstack rounded-bottom-0" closeButton={true} closeLabel="Close">
        <h4>{title}</h4>
      </Modal.Header>
      <Modal.Body>
        <Form id="SectionPropertiesForm" ref={formRef}>
          <Tabs id="section-properties" 
            activeKey={activeTab} 
            onSelect={(tk) => setActiveTab(tk ?? TabNames.General)}
            className="m-0 p-0 mt-2">
            <Tab eventKey={TabNames.General} title={TabNames.General} className="ms-2 me-2">
              <Row>
                <Col xs={12} md={6} className="mt-3">
                  <FormLabel>Section Template</FormLabel>
                  <FormSelect title="Select layout template" value={sectionDetail.Template} onChange={setSelectInput} name="Template">
                    <option value="" disabled>Select Section Template</option>
                    <option value={TemplateType.ColumnsFW}>Columns Full Width</option>
                    <option value={TemplateType.ColumnsCC}>Columns Centered</option>
                    <option value={TemplateType.ContentFW}>Full Width Content</option>
                    <option value={TemplateType.ContentCC}>Centered Content</option>
                  </FormSelect>              
                </Col>
                <Col xs={12} md={6} className="mt-3">
                  {sectionDetail.Template?.includes("Columns") && (
                    <>
                      <FormLabel>Columns</FormLabel>
                      <FormSelect title="Select number of columns" value={sectionDetail.Columns} onChange={setColumns} name="Columns">
                        {[1,2,3,4,6,12].map(num => (
                          <option key={`COL-${num}`} value={num}>{num}</option>
                        ))}
                      </FormSelect>
                    </>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3">
                  <FormLabel>Background Image</FormLabel>
                  <SiteMediaPicker 
                    existingUrl={sectionDetail.BackgroundImage} 
                    onMediaClear={() => updateProperty("BackgroundImage", "")}
                    onMediaSelect={(url) => updateProperty("BackgroundImage", url)}
                    key={"SECTION-IMAGE-SELECTOR"}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} className="mt-3">
                  <FormLabel>Background Colour</FormLabel>
                  <ColourPicker 
                    onColourChange={(colour) => updateProperty("BackgroundColour", colour ?? "")}
                    selectedColour={sectionDetail.BackgroundColour}
                    key={"SECTION-COLOUR-PICKER"}
                  />
                </Col>
              </Row>
            </Tab>
            <Tab eventKey={TabNames.Advanced} title={TabNames.Advanced}>
              <Row>
                <Col xs={12} md={6} className="mt-3">
                  <FormLabel>HRef ID (for links within a page)</FormLabel>
                  <FormControl type="text" name="SectionHRefId" onChange={setInput} value={sectionDetail.SectionHRefId ?? ""} ></FormControl>
                </Col>
                <Col xs={12} md={6} className="mt-3">
                  <FormLabel>Section Class (from your CSS or Bootstrap)</FormLabel>
                  <FormControl type="input" name="SectionClass" onChange={setInput} value={sectionDetail.SectionClass}></FormControl>
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="mt-3">
                  <FormLabel>Client JavaScript (runs when page loads)</FormLabel>
                  <AceEditor
                    mode="javascript"
                    theme="sqlserver"
                    name="ClientJS"
                    onChange={setCodeInput}
                    fontSize={12}
                    value={sectionDetail.ClientScript}
                    className={"border"}
                    setOptions={{
                      enableBasicAutocompletion: true,
                      enableLiveAutocompletion: true,
                      enableSnippets: true,
                      useWorker: false,
                      showPrintMargin: false
                    }}
                    style={{width: '100%', height:'230px'}} />
                  
                 </Col>
              </Row>
              <Row>
                <Col xs={12} className="mt-3">
                  <FormLabel>Hosted Client JavaScript (path to hosted js files)</FormLabel>
                  <FormControl type="input" onChange={setInput}></FormControl>
                </Col>
              </Row>
            </Tab>
          </Tabs>
         </Form>         
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={onSave}>Save</Button>
        <Button variant="secondary" onClick={props.onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddEditSection;
